import React from 'react';
import { Seat, Section } from './types';
import { MeetingRoom } from './meetingRoomTypes';
import { useSeatSelection } from './useSeatSelection';
import { isSelectable, MAX_SELECTED_ROOMS, isRoomSeat } from './rules';
import { FloorplanRenderer } from './FloorplanRenderer';
import { floorplanData } from './floorplan.config';
import { SeatComponent } from './SeatComponent';
import { MeetingRoomComponent } from './MeetingRoomComponent';
import { SECTION_COLORS } from './constants';
import { AvailabilityService } from '../services/AvailabilityService';
import { MeetingRoomService } from '../services/MeetingRoomService';
import { QuoteService } from '../hooks/useQuote';
import { sections, generateSeatMapData } from './seatmap.config';
import { DateTimeSelector } from './DateTimeSelector';
import { MeetingRoomDateTimeSelector } from './MeetingRoomDateTimeSelector';
import { adjustCrossMidnightDate } from '../utils/dateTimeUtils';
import { ApiDesk } from '../models/Desk';

interface SeatMapProps {
  onNavigateToCheckout?: (checkoutData: any) => void;
  onBack?: () => void;
}

export function SeatMap({ onNavigateToCheckout, onBack }: SeatMapProps) {
  const [seats, setSeats] = React.useState<Seat[]>([]);
  const [meetingRoom, setMeetingRoom] = React.useState<MeetingRoom | null>(null);
  const [isMeetingRoomSelected, setIsMeetingRoomSelected] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const isInitializingRef = React.useRef(true);
  
  // const isProduction = process.env.REACT_APP_ENVIRONMENT === \'production\';
  const {
    selectedSeatIds,
    selectedSeats,
    toggleSeat,
    clearSelection,
    isSeatSelected,
    canSelectMoreRooms,
    selectedRoomSeats,
    fullySelectedSectionIds,
  } = useSeatSelection(seats);

  // Booking state - simplified to only what's needed
  const [startDate, setStartDate] = React.useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [timeSlot, setTimeSlot] = React.useState('');
  const [bookingType, setBookingType] = React.useState<'Daily' | 'Monthly'>('Daily');
  const [quoteError, setQuoteError] = React.useState<string | null>(null);
  const [isQuoteLoading, setIsQuoteLoading] = React.useState(false);

  // Meeting room booking state
  const [meetingStartDate, setMeetingStartDate] = React.useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [meetingEndDate, setMeetingEndDate] = React.useState('');
  const [meetingStartTime, setMeetingStartTime] = React.useState('');
  const [meetingEndTime, setMeetingEndTime] = React.useState('');

  // Cabin mapping from API to UI sections (removed SANGAMA as it's now a meeting room)
  const cabinToSectionMapping: Record<string, string> = {
    'Varada': 'VARADA',
    'Sharavathi': 'SHARAVATHI',
    'Krishna': 'KRISHNA',
    'Bhadra': 'BHADRA',
    'Bhima': 'BHIMA',
    'Tunga': 'TUNGA',
    'Cauvery': 'CAUVERY',
    'hotdesk': 'OPEN_4SEATER'
  };

  const mapDesksToSeats = (apiDesks: ApiDesk[]): Seat[] => {
    const { seats: templateSeats } = generateSeatMapData();
    const mappedSeats: Seat[] = [];
    
    const hotdesks = apiDesks.filter(desk => desk.cabin === 'hotdesk');
    const cabinDesks = apiDesks.filter(desk => desk.cabin !== 'hotdesk');
    
    const cabinDesksBySectionId = cabinDesks.reduce((acc, desk) => {
      const sectionId = cabinToSectionMapping[desk.cabin];
      if (sectionId) {
        if (!acc[sectionId]) acc[sectionId] = [];
        acc[sectionId].push(desk);
      }
      return acc;
    }, {} as Record<string, ApiDesk[]>);
    
    const sectionCapacities = {
      'OPEN_3SEATER_1': 3,
      'OPEN_3SEATER_2': 3,
      'OPEN_2SEATER': 2,
      'OPEN_4SEATER': 4
    };
    
    const distributedHotdesks: Record<string, ApiDesk[]> = {
      'OPEN_3SEATER_1': [],
      'OPEN_3SEATER_2': [],
      'OPEN_2SEATER': [],
      'OPEN_4SEATER': []
    };
    
    let hotdeskIndex = 0;
    Object.entries(sectionCapacities).forEach(([sectionId, capacity]) => {
      for (let i = 0; i < capacity && hotdeskIndex < hotdesks.length; i++) {
        distributedHotdesks[sectionId].push(hotdesks[hotdeskIndex]);
        hotdeskIndex++;
      }
    });
    
    const allDesksBySection = { ...cabinDesksBySectionId, ...distributedHotdesks };

    Object.entries(allDesksBySection).forEach(([sectionId, sectionDesks]) => {
      const templateSeatsForSection = templateSeats.filter(seat => seat.sectionId === sectionId);
      
      sectionDesks.forEach((desk, index) => {
        const templateSeat = templateSeatsForSection[index];
        if (templateSeat) {
          // Use is_available flag to determine seat status
          // If is_available is false, mark as booked (unavailable for selection)
          // If is_available is true and status is active, mark as available
          let seatStatus: 'available' | 'booked' | 'blocked';
          
          if (!desk.is_available) {
            seatStatus = 'booked'; // Desk is not available (already booked)
          } else if (desk.status === 'active') {
            seatStatus = 'available'; // Desk is available for booking
          } else {
            seatStatus = 'blocked'; // Desk is inactive/blocked
          }
          
          mappedSeats.push({
            ...templateSeat,
            id: desk.id,
            code: desk.code,
            status: seatStatus
          });
        }
      });
    });

    return mappedSeats;
  };

  // Initial load on mount - single API call
  React.useEffect(() => {
    const loadInitialDesks = async () => {
      // Production check removed - feature now available in all environments
      
      try {
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        
        const nextHour = new Date(now);
        nextHour.setHours(now.getHours() + 1, 0, 0, 0);
        const startTime = nextHour.toTimeString().split(' ')[0];
        
        const endDateTime = new Date(nextHour);
        endDateTime.setHours(nextHour.getHours() + 24);
        const endDate = endDateTime.toISOString().split('T')[0];
        const endTime = endDateTime.toTimeString().split(' ')[0];
        
        const adjustedParams = adjustCrossMidnightDate({
          startDate: currentDate,
          endDate,
          startTime,
          endTime
        });

        // Call both APIs together on initial load
        const [availableDesks, meetingRoomAvailability] = await Promise.all([
          AvailabilityService.getDesksAvailability({
            start_date: adjustedParams.startDate,
            end_date: adjustedParams.endDate,
            start_time: adjustedParams.startTime,
            end_time: adjustedParams.endTime
          }),
          MeetingRoomService.checkAvailability({
            start_date: adjustedParams.startDate,
            start_time: adjustedParams.startTime,
            end_date: adjustedParams.endDate,
            end_time: adjustedParams.endTime
          })
        ]);
        
        const mappedSeats = mapDesksToSeats(availableDesks as any[]);
        setSeats(mappedSeats);
        
        // Initialize meeting room with availability
        const sangamaMeetingRoom: MeetingRoom = {
          id: 'SANGAMA',
          name: 'SANGAMA',
          isAvailable: meetingRoomAvailability.is_available,
          x: 263,
          y: 263,
          width: 150,
          height: 217
        };
        setMeetingRoom(sangamaMeetingRoom);
        
        // Allow DateTimeSelector to initialize, then enable user-triggered API calls
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 2000);
      } catch (error) {
        console.error('Error loading initial data:', error);
        // Initialize meeting room as unavailable on error
        const sangamaMeetingRoom: MeetingRoom = {
          id: 'SANGAMA',
          name: 'SANGAMA',
          isAvailable: false,
          x: 263,
          y: 263,
          width: 150,
          height: 217
        };
        setMeetingRoom(sangamaMeetingRoom);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialDesks();
  }, []);

  // Handle monthly booking type - auto-set end date to 30 days from start date
  React.useEffect(() => {
    console.log('Monthly booking useEffect triggered:', { bookingType, startDate });
    if (bookingType === 'Monthly' && startDate) {
      const start = new Date(startDate);
      const startMonth = start.getMonth();
      const isFebruary = startMonth === 1;
      
      // For February, add 28 days; for other months, add 30 days
      const daysToAdd = isFebruary ? 28 : 30;
      
      const endDateObj = new Date(start);
      endDateObj.setDate(start.getDate() + daysToAdd);
      
      const calculatedEndDate = endDateObj.toISOString().split('T')[0];
      console.log('Setting end date to:', calculatedEndDate);
      setEndDate(calculatedEndDate);
      
      // Set end time to match start time for monthly bookings
      if (startTime) {
        setEndTime(startTime);
      }
    }
  }, [bookingType, startDate, startTime]);

  // Fetch desk availability when user changes desk date/time (skip during initialization)
  React.useEffect(() => {
    if (!isInitializingRef.current && startDate && endDate && startTime && endTime) {
      const timer = setTimeout(async () => {
        setIsLoading(true);
        try {
          const adjustedParams = adjustCrossMidnightDate({
            startDate,
            endDate,
            startTime,
            endTime
          });

          // Only call desk availability API when desk dates change
          const availableDesks = await AvailabilityService.getDesksAvailability({
            start_date: adjustedParams.startDate,
            end_date: adjustedParams.endDate,
            start_time: adjustedParams.startTime,
            end_time: adjustedParams.endTime
          });
          
          // Update seats
          const mappedSeats = mapDesksToSeats(availableDesks as any[]);
          setSeats(mappedSeats);
        } catch (error) {
          console.error('Error fetching desk availability:', error);
        } finally {
          setIsLoading(false);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, startTime, endTime]);

  // Fetch meeting room availability when user changes meeting room date/time (skip during initialization)
  React.useEffect(() => {
    if (!isInitializingRef.current && meetingStartDate && meetingEndDate && meetingStartTime && meetingEndTime) {
      const timer = setTimeout(async () => {
        try {
          // Only call meeting room availability API when meeting room dates change
          const meetingRoomAvailability = await MeetingRoomService.checkAvailability({
            start_date: meetingStartDate,
            start_time: meetingStartTime,
            end_date: meetingEndDate,
            end_time: meetingEndTime
          });
          
          // Update meeting room availability
          setMeetingRoom(prev => prev ? {
            ...prev,
            isAvailable: meetingRoomAvailability.is_available
          } : null);
        } catch (error) {
          console.error('Error fetching meeting room availability:', error);
          // On error, assume meeting room unavailable
          setMeetingRoom(prev => prev ? {
            ...prev,
            isAvailable: false
          } : null);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [meetingStartDate, meetingEndDate, meetingStartTime, meetingEndTime]);

  const handleSeatClick = React.useCallback((seat: Seat) => {
    if (!isSelectable(seat)) {
      return;
    }
    
    if (isSeatSelected(seat.id)) {
      toggleSeat(seat.id);
    } else {
      const isRoom = isRoomSeat(seat);
      if (isRoom && !canSelectMoreRooms) {
        return;
      }
      toggleSeat(seat.id);
    }
  }, [isSeatSelected, toggleSeat, canSelectMoreRooms]);

  const handleMeetingRoomClick = React.useCallback((room: MeetingRoom) => {
    if (room.isAvailable) {
      setIsMeetingRoomSelected(prev => !prev);
    }
  }, []);

  const getSectionStyles = React.useCallback((section: Section) => {
    const fullySelected = fullySelectedSectionIds.has(section.id);
    
    if (fullySelected) {
      return {
        ...SECTION_COLORS.fullySelected,
        pointerEvents: 'none' as const,
      };
    }
    
    return {
      ...SECTION_COLORS.default,
      pointerEvents: 'none' as const,
    };
  }, [fullySelectedSectionIds]);

  const handleContinue = React.useCallback(async () => {
    if (!onNavigateToCheckout || (selectedSeatIds.size === 0 && !isMeetingRoomSelected)) {
      return;
    }

    setIsQuoteLoading(true);
    setQuoteError(null);

    try {
      // Prepare checkout data
      const checkoutData: any = {
        selectedSeats,
        startDate,
        endDate,
        startTime: startTime ? `${startTime}:00` : '00:00:00',
        endTime: endTime ? `${endTime}:00` : '00:00:00',
        timeSlot,
        bookingType
      };

      // Add meeting room data if selected
      if (isMeetingRoomSelected && meetingRoom) {
        checkoutData.meetingRoom = {
          id: meetingRoom.id,
          name: meetingRoom.name,
          startDate: meetingStartDate,
          endDate: meetingEndDate,
          startTime: meetingStartTime ? `${meetingStartTime}:00` : '00:00:00',
          endTime: meetingEndTime ? `${meetingEndTime}:00` : '00:00:00'
        };
      }

      // Prepare quote parameters
      const quoteParams: any = {};
      
      // Add desk parameters if seats are selected
      if (selectedSeatIds.size > 0) {
        const deskCodes = selectedSeats.map(seat => seat.code || seat.id);
        const apiBookingType = bookingType === 'Daily' ? 'daily' : 'monthly';
        
        const formatTimeToSeconds = (time: string): string => {
          if (!time) return '00:00:00';
          const [hours, minutes] = time.split(':');
          return `${hours}:${minutes}:00`;
        };
        
        quoteParams.deskCodes = deskCodes;
        quoteParams.deskBookingType = apiBookingType;
        quoteParams.deskStartDate = startDate;
        quoteParams.deskStartTime = formatTimeToSeconds(startTime);
        quoteParams.deskEndDate = endDate;
        quoteParams.deskEndTime = formatTimeToSeconds(endTime);
      }
      
      // Add meeting room parameters if meeting room is selected
      if (isMeetingRoomSelected && meetingRoom) {
        quoteParams.meetingRoomId = meetingRoom.id;
        quoteParams.meetingRoomStartDate = meetingStartDate;
        quoteParams.meetingRoomStartTime = meetingStartTime;
        quoteParams.meetingRoomEndDate = meetingEndDate;
        quoteParams.meetingRoomEndTime = meetingEndTime;
      }
      
      // Get quote with new API structure
      const quote = await QuoteService.getQuote(quoteParams);
      
      checkoutData.quote = quote;
      
      // Navigate to checkout with all data
      onNavigateToCheckout(checkoutData);
    } catch (error: any) {
      setQuoteError(error.message || 'Failed to get quote. Please try again.');
    } finally {
      setIsQuoteLoading(false);
    }
  }, [onNavigateToCheckout, selectedSeats, selectedSeatIds.size, isMeetingRoomSelected, meetingRoom, startDate, endDate, startTime, endTime, timeSlot, bookingType, meetingStartDate, meetingEndDate, meetingStartTime, meetingEndTime]);

  // Clear selection when dates change
  React.useEffect(() => {
    if (startDate && endDate) {
      clearSelection();
    }
  }, [startDate, endDate]);

  // Clear meeting room selection when meeting room dates change
  React.useEffect(() => {
    if (meetingStartDate && meetingEndDate) {
      setIsMeetingRoomSelected(false);
    }
  }, [meetingStartDate, meetingEndDate]);

  return (
    <div className="flex items-start justify-center p-4 md:p-8 min-h-screen bg-gray-100">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="fixed top-4 left-4 z-20 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 text-gray-700 hover:text-gray-900"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7"/>
            <path d="m19 12H5"/>
          </svg>
          Back
        </button>
      )}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-screen gap-6">
          {/* Animated desk illustration */}
          <svg width="120" height="90" viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Monitor */}
            <rect x="38" y="4" width="44" height="30" rx="3" fill="#1F473E" opacity="0.15">
              <animate attributeName="opacity" values="0.15;0.4;0.15" dur="1.4s" repeatCount="indefinite" />
            </rect>
            <rect x="38" y="4" width="44" height="30" rx="3" stroke="#1F473E" strokeWidth="2.5" fill="none" />
            <line x1="60" y1="34" x2="60" y2="40" stroke="#1F473E" strokeWidth="2.5" />
            <rect x="50" y="40" width="20" height="3" rx="1.5" fill="#1F473E" />
            {/* Desk surface */}
            <rect x="14" y="44" width="92" height="6" rx="3" fill="#1F473E" />
            {/* Desk legs */}
            <rect x="20" y="50" width="5" height="22" rx="2" fill="#1F473E" opacity="0.7" />
            <rect x="95" y="50" width="5" height="22" rx="2" fill="#1F473E" opacity="0.7" />
            {/* Chair seat */}
            <rect x="42" y="66" width="36" height="8" rx="4" fill="#1F473E" opacity="0.5">
              <animate attributeName="opacity" values="0.5;0.85;0.5" dur="1.4s" repeatCount="indefinite" begin="0.2s" />
            </rect>
            {/* Chair back */}
            <rect x="50" y="56" width="20" height="12" rx="3" fill="#1F473E" opacity="0.35">
              <animate attributeName="opacity" values="0.35;0.65;0.35" dur="1.4s" repeatCount="indefinite" begin="0.4s" />
            </rect>
            {/* Chair legs */}
            <line x1="48" y1="74" x2="44" y2="86" stroke="#1F473E" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
            <line x1="72" y1="74" x2="76" y2="86" stroke="#1F473E" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          </svg>

          {/* Pulsing dots */}
          <div className="flex items-center gap-2">
            {[0, 0.2, 0.4].map((delay, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: '#1F473E',
                  animation: `seatmap-pulse 1.2s ease-in-out ${delay}s infinite`,
                }}
              />
            ))}
          </div>

          <p style={{ color: '#1F473E', fontWeight: 600, fontSize: 15, letterSpacing: '0.04em' }}>
            Loading desks…
          </p>

          <style>{`
            @keyframes seatmap-pulse {
              0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
              40% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      ) : (
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full max-w-7xl relative">
        <div className="absolute left-1/2 transform -translate-x-1/2 z-10 -mt-3">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#1F473E' }}></div>
              <span className="text-sm text-gray-700">Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-400"></div>
              <span className="text-sm text-gray-700">Unavailable</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-300"></div>
              <span className="text-sm text-gray-700">Available</span>
            </div>
          </div>
        </div>
        {/* Map Section */}
        <div className="flex-1 bg-white rounded-lg border-2 border-gray-300 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden relative order-2 lg:order-1 mt-8">
          <svg
            viewBox={`0 0 ${floorplanData.viewBox.width} ${floorplanData.viewBox.height}`}
            className="w-full h-auto block transition-opacity duration-200"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Data-driven floorplan background */}
            <FloorplanRenderer floorplan={floorplanData} />

            {/* Section outlines (overlay on floorplan) */}
            {sections.map((section) => (
              <path
                key={section.id}
                d={section.pathD}
                className="transition-all duration-200"
                {...getSectionStyles(section)}
              />
            ))}

            {/* Seats */}
            {seats.map((seat) => (
              <SeatComponent
                key={seat.id}
                seat={seat}
                isSelected={isSeatSelected(seat.id)}
                onSelect={handleSeatClick}
              />
            ))}

            {/* Meeting Room */}
            {meetingRoom && (
              <MeetingRoomComponent
                meetingRoom={meetingRoom}
                isSelected={isMeetingRoomSelected}
                onSelect={handleMeetingRoomClick}
              />
            )}
          </svg>
        </div>

        {/* Controls Section */}
        <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4 order-1 lg:order-2 lg:sticky lg:top-8 mt-8">
          {/* Booking Details Card */}
          <div className="flex flex-col gap-4 p-4 lg:p-6 bg-white rounded-lg shadow-md">
            <div className="text-lg font-medium text-gray-800">Booking Details</div>
            
            {/* Booking Type */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Booking Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bookingType"
                    value="Daily"
                    checked={bookingType === 'Daily'}
                    onChange={(e) => setBookingType(e.target.value as 'Daily' | 'Monthly')}
                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Daily</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bookingType"
                    value="Monthly"
                    checked={bookingType === 'Monthly'}
                    onChange={(e) => setBookingType(e.target.value as 'Daily' | 'Monthly')}
                    className="w-4 h-4 text-red-500 border-gray-300 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Monthly</span>
                </label>
              </div>
            </div>

            {/* Date & Time Selection */}
            <DateTimeSelector 
              startDate={startDate}
              endDate={endDate}
              startTime={startTime}
              endTime={endTime}
              bookingType={bookingType}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
            />
          </div>

          {/* Meeting Room Booking Card */}
          <div className="flex flex-col gap-4 p-4 lg:p-6 bg-white rounded-lg shadow-md">
            <div className="text-lg font-medium text-gray-800">Meeting Room Booking</div>
            
            {/* Meeting Room Date & Time Selection */}
            <MeetingRoomDateTimeSelector 
              startDate={meetingStartDate}
              endDate={meetingEndDate}
              startTime={meetingStartTime}
              endTime={meetingEndTime}
              onStartDateChange={setMeetingStartDate}
              onEndDateChange={setMeetingEndDate}
              onStartTimeChange={setMeetingStartTime}
              onEndTimeChange={setMeetingEndTime}
            />
          </div>

          {(selectedSeatIds.size > 0 || isMeetingRoomSelected) && (
            <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-md">
              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-700">Selected Items:</div>
                <div className="flex flex-wrap gap-2">
                  {selectedSeats.map((seat) => (
                    <span
                      key={seat.code}
                      className="inline-block px-3 py-1.5 text-white rounded-full text-sm font-medium"
                      style={{ backgroundColor: '#1F473E' }}
                    >
                      {seat.code}
                    </span>
                  ))}
                  {isMeetingRoomSelected && (
                    <span
                      className="inline-block px-3 py-1.5 text-white rounded-full text-sm font-medium"
                      style={{ backgroundColor: '#1F473E' }}
                    >
                      SANGAMA (Meeting Room)
                    </span>
                  )}
                </div>
              </div>
              <button
                className="w-full px-6 py-2 bg-red-500 text-white border-none rounded text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-red-600 active:bg-red-700"
                onClick={() => {
                  clearSelection();
                  setIsMeetingRoomSelected(false);
                }}
                type="button"
              >
                Clear Selection
              </button>
               <button
                className={`w-full px-6 py-2 border-none rounded text-base font-medium cursor-pointer transition-colors duration-200 ${
                  (selectedSeatIds.size > 0 || isMeetingRoomSelected) && !isQuoteLoading
                    ? 'text-white hover:opacity-90 active:opacity-80'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={{
                  backgroundColor: (selectedSeatIds.size > 0 || isMeetingRoomSelected) && !isQuoteLoading ? '#1F473E' : undefined
                }}
                onClick={handleContinue}
                disabled={(selectedSeatIds.size === 0 && !isMeetingRoomSelected) || isQuoteLoading}
                type="button"
              >
                {isQuoteLoading ? 'Getting Quote...' : 'Continue'}
              </button>
              {quoteError && (
                <div className="text-xs text-red-500 text-center">
                  {quoteError}
                </div>
              )}

            </div>
          )}
          {selectedSeatIds.size === 0 && !isMeetingRoomSelected && (
            <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-md">
              <div className="text-sm text-gray-500 italic">
                No seats or meeting room selected. Click on available seats or meeting room to select them.
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}